import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SelectedAppraiser, SelectedPractice } from "./models";
import {
  AppraiserMapData,
  PracticeMapData,
} from "../../../../redux/apiSpecifications/apiCrud";
import { useEffect, useState } from "react";
import { getAzureMapAddressConfig } from "../../../../config/azureConfig";
import i18next from "i18next";

const MapContainerWrapper = styled.div`
  flex-direction: column;

  display: flex;
  background-color: #fff;
  border-radius: 1em;
  box-shadow: 0 0 5px #aaa;
  overflow: hidden;

  .header {
    color: #333;
    background-color: #ededed;

    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: bold;

    display: flex;

    .title {
      flex: 1 1 0%;
      padding: 0.5em 2em;
    }

    .actions {
      .icon-wrapper {
        font-size: 1.4em;
        margin-top: 6px;
        margin-right: 1em;
        cursor: pointer;
      }
    }
  }

  .body {
    display: flex;
    width: 100%;
    justify-content: space-between;

    .col {
      flex: 1;
    }

    .map {
      width: 100%;
    }
  }

  .popup-close {
    color: white;
  }
`;

const getLanguageCode = (language: string) =>
  language === "it" ? "it-IT" : language === "en" ? "en-US" : "en-US";

interface IPracticeMapProps {
  practice?: PracticeMapData;
  appraisers?: AppraiserMapData[] | undefined;
}

type MarkerData<T> = {
  type: "Appraiser" | "Practice";
  icon: string;
  info: T;
};

type Marker = {
  longitude: number;
  latitude: number;
  data: MarkerData<SelectedAppraiser | SelectedPractice>;
  onClick?: (m: Marker) => void;
};

const buildHtmlContent = (
  selectedPracticeProvince: string,
  data: MarkerData<SelectedAppraiser | SelectedPractice>
) => {
  if (data.type === "Appraiser") {
    const info = data.info as SelectedAppraiser;

    const isInZone = selectedPracticeProvince === info.districtCode;

    let imageSrc;

    if (info.isPosition) {
      imageSrc = info.isInternal
        ? isInZone
          ? `/images/markers/new/appraisers/internal-${info.saturationClusterCode}-live-big.png`
          : `/images/markers/new/appraisers/internal-out-big.png`
        : isInZone
        ? `/images/markers/new/appraisers/external-${info.saturationClusterCode}-live-big.png`
        : `/images/markers/new/appraisers/external-out-big.png`;
    } else {
      imageSrc = info.isInternal
        ? isInZone
          ? `/images/markers/new/appraisers/internal-${info.saturationClusterCode}-big.png`
          : `/images/markers/new/appraisers/internal-out-big.png`
        : isInZone
        ? `/images/markers/new/appraisers/external-${info.saturationClusterCode}-big.png`
        : `/images/markers/new/appraisers/external-out-big.png`;
    }

    return `<img src="${imageSrc}"/>`;
  } else {
    const practice = data.info as SelectedPractice;
    return `<img src="/images/markers/new/practices/out-${practice.status}-big.png"/>`;
  }
};

const PracticeMap = ({ practice, appraisers }: IPracticeMapProps) => {
  const wrapperId: string = "myMap";

  const { t } = useTranslation();

  const [map, setMap] = useState<any>();
  const [atlas, setAtlas] = useState<any>();

  useEffect(() => {
    const azureMapAddressConfig = getAzureMapAddressConfig();

    import("azure-maps-control").then((_atlas) => {
      const mapInstance = new _atlas.Map(wrapperId, {
        language: getLanguageCode(i18next.language),
        authOptions: {
          authType: _atlas.AuthenticationType.subscriptionKey,
          subscriptionKey: azureMapAddressConfig.subscriptionKey,
        },
        showLogo: false,
        showFeedbackLink: false,
        style: "grayscale_light",
      });

      setMap(mapInstance);
      setAtlas(_atlas);
    });
  }, []);

  useEffect(() => {
    if (!map || !atlas || !practice || !appraisers) return;

    map.popups.clear();
    map.markers.clear();

    const markers: Marker[] = [];

    if (practice) {
      const practiceMarker: Marker = {
        longitude: practice.appraisalLocationAddress?.longitude!,
        latitude: practice.appraisalLocationAddress?.latitude!,
        data: {
          type: "Practice",
          icon: `Practice_Gray_${practice.practiceStatus}`,
          info: {
            id: practice.id,
            number: practice.practiceNumber,
            status: practice.practiceStatus,
            longitude: practice?.appraisalLocationAddress?.longitude,
            latitude: practice?.appraisalLocationAddress?.latitude,
            assignedAppraiser: practice.assignedAppraiser,
            assignedAppraiserId: practice.assignedAppraiserId,
            appraisalLocationAddress: `${practice.appraisalLocationAddress?.street}, ${practice.appraisalLocationAddress?.civic} ${practice.appraisalLocationAddress?.city} (${practice.appraisalLocationAddress?.province})`,
            province: practice.appraisalLocationAddress?.province,
            receiptDate: practice.receiptDate,
            practiceType: practice.practiceTypeCode,
          } as SelectedPractice,
        },
      };

      markers.push(practiceMarker);
    }

    appraisers.forEach((appraiser) => {
      appraiser.coverageDistricts!.forEach((coverageDistrict) => {
        const appraisersMarker: Marker = {
          longitude: coverageDistrict.longitude!,
          latitude: coverageDistrict.latitude!,
          data: {
            type: "Appraiser",
            icon: `Appraiser_${coverageDistrict.saturationClusterCode}`,
            info: {
              id: appraiser.id,
              name: appraiser.name,
              idZone: coverageDistrict.idzone,
              longitude: coverageDistrict.longitude,
              latitude: coverageDistrict.latitude,
              saturationClusterCode: coverageDistrict.saturationClusterCode,
              address: appraiser.address,
              totalManaged: appraiser.totalManagedPractices,
              dailyCapacity: appraiser.totalDailyCapacity,
              montlyResidual: appraiser.monthlyResidual,
              monthlyCapacity: appraiser.totalMonthlyCapacity,
              score: appraiser.totalScore,
              districtCode: coverageDistrict.districtCode,
              isPosition: coverageDistrict.isPosition,
            } as SelectedAppraiser,
          },
        };
        markers.push(appraisersMarker);
      });
    });

    map.events.add("ready", () => {
      const popup = new atlas.Popup({
        position: [0, 0],
        pixelOffset: [0, -18],
      });

      markers.forEach((marker) => {
        const htmlMarker = new atlas.HtmlMarker({
          htmlContent: buildHtmlContent(
            practice.appraisalLocationAddress?.province!,
            marker.data
          ),
          position: [marker.longitude, marker.latitude],
        });

        map.markers.add(htmlMarker);

        map.events.add("mousemove", htmlMarker, () => {
          const html: string[] = ['<div style="padding:10px;">'];

          if (marker.data.type === "Practice") {
            const practice = marker.data.info as SelectedPractice;
            html.push(practice.number!);
          } else if (marker.data.type === "Appraiser") {
            const appraiser = marker.data.info as SelectedAppraiser;
            html.push(appraiser.name);
          }

          html.push("</div>");

          popup.setOptions({
            position: [marker.longitude, marker.latitude],
            content: html.join(""),
          });

          popup.open(map);
        });

        map.events.add("mouseout", htmlMarker, () => {
          popup.close();
        });
      });

      const bounds = atlas.data.BoundingBox.fromPositions(
        markers.map((m) => [m.longitude, m.latitude])
      );

      map.setCamera({
        bounds,
        padding: 50,
      });
    });
  }, [map, practice, appraisers]);

  return (
    <MapContainerWrapper>
      <div className="header">
        <div className="title">{t("assignment-map-label")}</div>
      </div>

      <div className="body">
        <div
          id={wrapperId}
          className="map"
          style={{
            aspectRatio: "1 / 1",
            visibility: map === undefined ? "hidden" : "visible",
          }}
        ></div>
      </div>
    </MapContainerWrapper>
  );
};

export default PracticeMap;
